import { faArrowsRotate, faBan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';
import VoxelButton from '../../../../../components/voxel/Buttons/VoxelButton';
import VoxelDialog from '../../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogContent from '../../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelTooltipShort from '../../../../../components/voxel/Tooltip/VoxelTooltipShort';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { UrlMatchTypeGq } from '../../../../../graphql/projects/models/UrlMatchTypeGq';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import TaskUrl from '../../Tables/ContentTaskTable/TaskUrl';

export interface MetaRuleListDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  setMetaRuleId: Dispatch<SetStateAction<number>>;
  reload: () => Promise<any>;
  project: ProjectGq;
  clientUrl: string;
  setMetaRuleOpen: Dispatch<SetStateAction<boolean>>;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      paddingBottom: '0px',
    },
    tableRow: {
      cursor: 'pointer',
    },
  })
);

const MetaRuleListDialog: React.FC<MetaRuleListDialogProps> = ({
  openDialog,
  setOpenDialog,
  project,
  setMetaRuleId,
  reload,
  clientUrl,
  setMetaRuleOpen,
}) => {
  //Close dialog
  const closeDialog = useCallback((event?: object, reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    } else {
      setOpenDialog(false);
    }
  }, []);

  // State
  const [activePage, setActivePage] = useState(0);

  const metaRulesQuery = projectsApiService.queries.useGetMetaRules(
    { projectId: project.id, page: activePage, rowsPerPage: 10 },
    { pollInterval: 5000 }
  );

  // Handle select rule from list
  const handleSelectRule = (event: any, selectedRuleId: number) => {
    setMetaRuleId(selectedRuleId);
  };

  //Styling
  const theme = useTheme();
  const classes = useClasses(theme);

  return (
    <VoxelDialog
      maxWClass={'max-w-4xl'}
      open={openDialog}
      onClose={() => setOpenDialog(false)}
      title={'Meta rule generator'}
      subtitle={
        'Create rules to automatically replace titles, H1s and meta descriptions for multiple pages at once.'
      }
    >
      <VoxelDialogContent>
        <div>
          <div className="flex justify-end items-center mb-1">
            <VoxelButton
              title={'New Rule'}
              endIcon={<Add fontSize="small" />}
              color="gray"
              size="sm"
              onClick={() => setMetaRuleOpen(true)}
            />
          </div>
          {metaRulesQuery?.metaRules?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={'100%'}>Rules</TableCell>
                    <TableCell align="left">Type</TableCell>
                    <TableCell align="center">Urls</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metaRulesQuery?.metaRules?.map((pagedRule) => (
                    <TableRow
                      key={pagedRule.id}
                      onClick={(event) => handleSelectRule(event, pagedRule.id)}
                      className={classes.tableRow}
                      hover
                    >
                      <TableCell>
                        {pagedRule?.urlMatchType === UrlMatchTypeGq.CONTAINS ? '...' : ''}
                        <TaskUrl contentTaskUrl={pagedRule?.url} clientUrl={clientUrl} />
                        {pagedRule?.urlMatchType === UrlMatchTypeGq.STARTS ||
                          pagedRule?.urlMatchType === UrlMatchTypeGq.CONTAINS
                          ? '...'
                          : ''}
                      </TableCell>
                      <TableCell align="left" className="whitespace-nowrap">
                        {pagedRule?.urlMatchType === UrlMatchTypeGq.EXACT && 'Exact match'}
                        {pagedRule?.urlMatchType === UrlMatchTypeGq.STARTS && 'Starting with'}
                        {pagedRule?.urlMatchType === UrlMatchTypeGq.CONTAINS && 'Contains'}
                      </TableCell>
                      <TableCell align="center">{pagedRule?.metaUrls?.length ?? 0}</TableCell>
                      <TableCell align="center">
                        {pagedRule?.active ? (
                          <VoxelTooltipShort
                            children={
                              <FontAwesomeIcon
                                icon={faArrowsRotate}
                                className={'text-success-500'}
                              />
                            }
                            message={'Active'}
                          />
                        ) : (
                          <VoxelTooltipShort
                            children={<FontAwesomeIcon icon={faBan} className={'text-error-500'} />}
                            message={'Draft'}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="caption">No rules have been added</Typography>
          )}
        </div>

        <TablePagination
          component={'div'}
          count={metaRulesQuery?.count ?? 0}
          page={metaRulesQuery?.page ?? 0}
          rowsPerPage={metaRulesQuery?.rowsPerPage}
          rowsPerPageOptions={[]}
          onPageChange={(event, page) => setActivePage(page)}
          onRowsPerPageChange={(event) => { }}
        />
      </VoxelDialogContent>
    </VoxelDialog>
  );
};

export default MetaRuleListDialog;
